import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Composant <ui-yearpicker>
 * Permet de sélectionner uniquement une année dans une grille 3x4
 */
export class UIYearPicker extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format YYYY
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    min: { type: String }, // Format YYYY
    max: { type: String }, // Format YYYY
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    currentYear: { type: Number, state: true },
    inputValue: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .yearpicker-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .yearpicker-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .yearpicker-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .yearpicker-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .yearpicker-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .yearpicker-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .yearpicker-icon {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .yearpicker-container.open .yearpicker-icon {
      color: var(--ui-primary);
    }

    /* Le Panel Popup */
    .yearpicker-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 280px;
      padding: 16px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
    }

    .yearpicker-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* En-tête du volet (navigation décennie) */
    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .decade-title {
      font-weight: 600;
      color: var(--ui-text);
      font-size: var(--ui-font-size-base);
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-text);
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .nav-btn:hover {
      background-color: var(--ui-surface-hover);
      border-color: var(--ui-border-hover);
      color: var(--ui-primary);
    }

    /* Grille 3x4 pour les 12 ans */
    .years-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }

    .year-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 42px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      font-weight: 500;
      transition: var(--ui-transition-fast);
    }

    .year-cell:hover:not(.disabled):not(.selected) {
      background-color: var(--ui-surface-hover);
      color: var(--ui-primary);
    }

    .year-cell.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    .year-cell.current-year:not(.selected) {
      border: 1.5px solid var(--ui-primary);
      color: var(--ui-primary);
      font-weight: 600;
    }

    .year-cell.disabled {
      cursor: not-allowed;
      opacity: 0.25;
      background-color: transparent;
    }

    /* Footer avec bouton Cette année */
    .panel-footer {
      margin-top: 14px;
      padding-top: 8px;
      border-top: 1px solid var(--ui-border);
      display: flex;
      justify-content: center;
    }

    .btn-this-year {
      width: 100%;
      height: 32px;
      border: 1px solid var(--ui-primary);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-primary);
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-this-year:hover {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    /* Style Responsive Mobile - Bottom Sheet */
    @media (max-width: 576px) {
      .yearpicker-container.open .backdrop {
        display: block;
      }

      .yearpicker-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
        box-sizing: border-box;
      }

      .yearpicker-container.open .yearpicker-panel {
        transform: translateY(0);
        opacity: 1;
      }

      .yearpicker-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -8px auto 16px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Sélectionner l\'année...';
    this.disabled = false;
    this.required = false;
    this.min = '';
    this.max = '';
    this.label = '';

    // États initiaux
    this.isOpen = false;
    this.inputValue = '';
    
    const today = new Date();
    this.currentYear = today.getFullYear();

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez sélectionner une année.', this.shadowRoot.querySelector('.yearpicker-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('value')) {
      if (this.value) {
        this.inputValue = this.value;
        this.currentYear = parseInt(this.value, 10);
      } else {
        this.inputValue = '';
      }
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    if (this.value) {
      this.currentYear = parseInt(this.value, 10);
    } else {
      this.currentYear = new Date().getFullYear();
    }
  }

  _closePanel() {
    this.isOpen = false;
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    const parsed = parseInt(this.inputValue.trim(), 10);

    if (parsed && !isNaN(parsed) && String(parsed).length === 4) {
      if (this._isYearDisabled(parsed)) {
        return;
      }
      const newISO = String(parsed);
      if (newISO !== this.value) {
        this.value = newISO;
        this.currentYear = parsed;
        this._dispatchChangeEvent(newISO);
      }
    }
  }

  _handleBlur() {
    if (this.value) {
      this.inputValue = this.value;
    } else {
      this.inputValue = '';
    }
  }

  _selectYear(year) {
    if (this._isYearDisabled(year)) return;

    const newISO = String(year);
    this.value = newISO;
    this._closePanel();
    this._dispatchChangeEvent(newISO);
  }

  _selectThisYear() {
    const y = new Date().getFullYear();
    if (this._isYearDisabled(y)) return;

    const newISO = String(y);
    this.value = newISO;
    this.currentYear = y;
    this._closePanel();
    this._dispatchChangeEvent(newISO);
  }

  _dispatchChangeEvent(isoValue) {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { value: isoValue },
      bubbles: true,
      composed: true
    }));
  }

  _isYearDisabled(year) {
    if (this.min) {
      const minYear = parseInt(this.min, 10);
      if (!isNaN(minYear) && year < minYear) return true;
    }
    if (this.max) {
      const maxYear = parseInt(this.max, 10);
      if (!isNaN(maxYear) && year > maxYear) return true;
    }
    return false;
  }

  _prevDecade() {
    this.currentYear -= 12;
  }

  _nextDecade() {
    this.currentYear += 12;
  }

  render() {
    const today = new Date();
    const parsedValue = this.value ? parseInt(this.value, 10) : null;
    
    // Grille de 12 ans centrés autour de currentYear
    const years = Array.from({ length: 12 }, (_, i) => this.currentYear - 5 + i);

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="yearpicker-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closePanel}"></div>

        <!-- En-tête avec input -->
        <div class="yearpicker-header">
          <input 
            type="text" 
            inputmode="none"
            class="yearpicker-input" part="input" 
            placeholder="${this.placeholder}"
            .value="${this.inputValue}"
            ?disabled="${this.disabled}"
            @focus="${this._openPanel}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          >
          <span class="yearpicker-icon" @click="${this._togglePanel}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
              <path d="M9 16l3-3 3 3"></path>
            </svg>
          </span>
        </div>

        <!-- Volet de sélection -->
        <div class="yearpicker-panel ${this.isOpen ? 'open' : ''}">
          <div class="panel-header">
            <button type="button" class="nav-btn" @click="${this._prevDecade}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <div class="decade-title">
              ${this.currentYear - 5} - ${this.currentYear + 6}
            </div>
            
            <button type="button" class="nav-btn" @click="${this._nextDecade}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>

          <div class="years-grid">
            ${years.map(y => {
              const isSelected = parsedValue === y;
              const isCurrent = today.getFullYear() === y;
              const isDisabled = this._isYearDisabled(y);

              return html`
                <div 
                  class="year-cell 
                    ${isSelected ? 'selected' : ''} 
                    ${isCurrent ? 'current-year' : ''} 
                    ${isDisabled ? 'disabled' : ''}"
                  @click="${() => this._selectYear(y)}"
                >
                  ${y}
                </div>
              `;
            })}
          </div>

          <div class="panel-footer">
            <button type="button" class="btn-this-year" @click="${this._selectThisYear}">Cette année</button>
          </div>
        </div>
      </div>
    `;
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this._closePanel();
    }
  }
}

if (!customElements.get('ui-yearpicker')) {
  customElements.define('ui-yearpicker', UIYearPicker);
}
